<template>
  <v-dialog :value="model.active" width="400" persistent>
    <v-card>
      <div class="d-flex justify-space-between align-center pr-2">
        <v-card-title class="text-h6 font-weight-bold">
          <v-icon left>mdi-upload</v-icon>
          Upload Images
        </v-card-title>
        <v-btn icon @click="closeDialog"><v-icon>fa-close</v-icon></v-btn>
      </div>
      <v-divider></v-divider>
      <v-card-text class="pt-5">
        <v-form>
          <div v-if="!isUploading" style="display: flex; flex-direction: column; height: 100%">
            <!-- Show project name as read-only when pre-selected, otherwise show dropdown -->
            <v-text-field
              v-if="preSelectedProject"
              outlined
              label="Project"
              dense
              hide-details
              :value="preSelectedProject.name"
              readonly
              disabled
              class="mb-2"></v-text-field>
            <v-select
              v-else
              outlined
              label="Projects"
              dense
              hide-details
              :items="projects"
              v-model="selected.targetProjectName"
              item-text="name"></v-select>

            <!-- Scan Type Selection -->
            <v-select
              v-model="scanType"
              :items="scanTypeOptions"
              item-text="text"
              item-value="value"
              label="Scan Type"
              outlined
              dense
              hide-details
              class="mt-2 mb-3"></v-select>

            <!-- Scan File Selection (not shown for GPS) -->
            <div v-if="scanType !== 'gps'" class="mb-3">
              <label class="text-body-2 mb-1 d-block">Scan File</label>
              <v-sheet
                height="60px"
                class="cursor-pointer d-flex align-center justify-center rounded-lg"
                style="border-style: dashed"
                outlined
                v-ripple
                @click.stop="clickOnSelectTextFile()">
                <div v-if="!scanFile" class="d-flex align-center" style="gap: 8px">
                  <v-icon>mdi-file-document-outline</v-icon>
                  <span>Click to select scan file (.txt)</span>
                </div>
                <div v-else class="d-flex align-center justify-space-between px-3" style="width: 100%">
                  <div class="d-flex align-center" style="gap: 8px">
                    <v-icon color="success">mdi-file-check</v-icon>
                    <div>
                      <div class="text-body-2 font-weight-medium text-truncate" style="max-width: 200px">
                        {{ scanFile.name }}
                      </div>
                      <div class="text-caption grey--text">{{ getSize(scanFile.size) }}</div>
                    </div>
                  </div>
                  <v-btn icon x-small @click.stop="clearScanFile">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </div>
              </v-sheet>
            </div>
            <input
              type="file"
              style="display: none"
              id="uploadScanFile"
              @change="scanFileSelected($event)"
              accept="text/plain,.txt" />

            <label class="mb-1">Images 360</label>

            <v-sheet
              height="100px"
              class="cursor-pointer d-flex align-center flex-column justify-center rounded-lg"
              style="border-style: dashed"
              outlined
              v-ripple
              @click.stop="clickOnSelectImagesFiles()">
              <v-icon class="mb-2" large>mdi-file-upload-outline</v-icon>
              <div>Click To Select Images</div>
            </v-sheet>
            <v-list v-if="!!images.length" max-height="150px" class="overflow-auto">
              <v-list-item
                dense
                class="bg1 mb-1 rounded-lg"
                v-for="file in images"
                :key="file.name + file.lastModified">
                <v-list-item-content>
                  <v-list-item-title>{{ file.name }}</v-list-item-title>
                  <v-list-item-subtitle>Size: {{ getSize(file.size) }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>

            <!-- Validation Stats (compact) -->
            <div v-if="validation.isValidating" class="mt-2 d-flex align-center" style="gap: 8px">
              <v-progress-circular indeterminate size="16" width="2" color="primary"></v-progress-circular>
              <span class="text-caption">Validating...</span>
            </div>
            <div
              v-else-if="validation.hasValidated && scanType !== 'gps'"
              class="mt-2 d-flex align-center justify-space-between">
              <div class="d-flex align-center" style="gap: 6px">
                <v-icon small :color="validation.alertType === 'success' ? 'success' : validation.alertType === 'error' ? 'error' : 'warning'">
                  {{ validation.alertType === 'success' ? 'mdi-check-circle' : validation.alertType === 'error' ? 'mdi-close-circle' : 'mdi-alert-circle' }}
                </v-icon>
                <span class="text-caption">
                  <strong>{{ validation.matched }}</strong>/{{ images.length }} valid
                </span>
              </div>
              <v-btn
                x-small
                text
                color="primary"
                @click="showValidationDialog = true">
                <v-icon x-small left>mdi-information-outline</v-icon>
                Details
              </v-btn>
            </div>

            <input
              type="file"
              style="display: none"
              id="multiple360Images"
              @change="imagesSelected($event)"
              accept="image/jpeg,image/png,.jpg,.jpeg,.png"
              multiple />
          </div>
          <CenteredText v-if="isUploading" :text="uploadingStatus"></CenteredText>

          <div
            v-if="isUploading && fileProgress < 100"
            class="d-flex flex-row align-center"
            style="gap: 4px">
            <v-progress-linear :value="fileProgress" color="primary" height="10" rounded>
            </v-progress-linear>
            {{ fileProgress }}%
          </div>
        </v-form>
      </v-card-text>
      <v-divider></v-divider>

      <v-card-actions>
        <v-btn color="red" elevation="0" text @click="closeDialog"> close </v-btn>
        <v-spacer></v-spacer>
        <v-btn elevation="0" :disabled="!model.hasChanges" @click="discard"> discard </v-btn>
        <v-btn
          color="primary"
          elevation="0"
          @click="upload"
          :disabled="!model.valid || !model.hasChanges || isUploading">
          Upload
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Validation Details Dialog -->
    <ValidationDialog
      v-model="showValidationDialog"
      :validation="validation"
      :scan-file-name="scanFile ? scanFile.name : ''" />
  </v-dialog>
</template>

<script>
  const THREE = window.THREE;
  import {
    getRotatedDirectionByNorthAngle,
    convertLatLongToXYZCoordinates_Indian,
    readFileContent,
  } from '@/scripts/js/forgeService';
  import { Image360 } from '@/scripts/js/forgeModels';
  import $ from 'jquery';
  import HttpService from '../../../utils/httpService';
  import CenteredText from '@/components/common/centeredText.vue';
  import ValidationDialog from './ValidationDialog.vue';
  import {
    SCAN_TYPE_OPTIONS,
    getScanTypeLabel,
    readScanFileContent,
    validateFilesAgainstScan,
    createInitialValidationState,
    formatFileSize,
  } from './scanValidation';
  import { $handleError, $isEqual } from '@/utils';
  import { log, warn, error } from '@/utils/debugLogger';
  export default {
    name: 'AdminUploadImagesPopup',

    data() {
      return {
        isUploading: false,
        uploadingStatus: 'Uploading...',
        scanType: 'faro',
        scanTypeOptions: SCAN_TYPE_OPTIONS,
        scanFile: null,
        scanFileContent: null,
        fileProgress: 0,
        images: [],
        validation: createInitialValidationState(),
        showValidationDialog: false,
        selected: {},
        selectedCemented: {},
        projects: [],
        preSelectedProject: null,
        model: {
          active: false,
          loading: false,
          valid: true,
          hasChanges: false,
        },
      };
    },
    methods: {
      open(projects, preSelectedProject = null) {
        // Reset all form data before opening
        this.resetForm();
        this.projects = projects;
        this.preSelectedProject = preSelectedProject;
        // If a project is pre-selected, set it as the target
        if (preSelectedProject) {
          this.selected.targetProjectName = preSelectedProject.name;
          this.selected.targetProjectId = preSelectedProject.id;
        }
        this.model.active = true;
        setTimeout(() => {
          this.cementSelected();
        }, 400);
      },
      resetForm() {
        // Reset form data
        this.selected = {};
        this.selectedCemented = {};
        this.images = [];
        this.scanFile = null;
        this.scanFileContent = null;
        this.scanType = 'faro';
        this.fileProgress = 0;
        this.isUploading = false;
        this.uploadingStatus = 'Uploading...';
        this.model.hasChanges = false;

        // Reset validation
        this.validation = createInitialValidationState();
        this.showValidationDialog = false;

        // Reset file inputs
        const scanInput = document.getElementById('uploadScanFile');
        const imagesInput = document.getElementById('multiple360Images');
        if (scanInput) scanInput.value = '';
        if (imagesInput) imagesInput.value = '';
      },
      getSize(size) {
        return formatFileSize(size);
      },
      reset() {
        this.model.active = false;
        this.preSelectedProject = null;
        this.resetForm();
      },
      closeDialog() {
        if (this.model.hasChanges) {
          const answer = window.confirm('You have unsaved changes on this page! leave anyway?');
          if (answer) {
            this.reset();
            this.discard();
          }
        } else {
          this.reset();
        }
      },
      discard() {
        this.selected = structuredClone(this.selectedCemented);
        this.model.hasChanges = false;
      },
      cementSelected() {
        this.selectedCemented = structuredClone(this.selected);
        this.checkForChanges();
      },
      checkForChanges() {
        if (!this.model.active) return;

        this.model.hasChanges = !$isEqual(this.selected, this.selectedCemented);
      },
      clickOnSelectTextFile() {
        document.getElementById('uploadScanFile').click();
      },
      clickOnSelectImagesFiles() {
        document.getElementById('multiple360Images').click();
      },
      imagesSelected($event) {
        this.images = $event.target.files;
        // Mark as changed when images are selected
        if (this.images.length > 0) {
          this.model.hasChanges = true;
          this.validateFiles();
        }
      },
      async scanFileSelected($event) {
        const files = $event.target.files;
        if (files && files.length > 0) {
          this.scanFile = files[0];
          this.model.hasChanges = true;

          // Read file content for validation
          try {
            this.scanFileContent = await readScanFileContent(this.scanFile);
            this.validateFiles();
          } catch (err) {
            console.error('Error reading scan file:', err);
            this.scanFileContent = null;
          }
        }
      },
      validateFiles() {
        // Only validate if we have both scan file content and images (and not GPS type)
        if (!this.scanFileContent || !this.images.length || this.scanType === 'gps') {
          this.validation.hasValidated = false;
          return;
        }

        this.validation.isValidating = true;

        // Use setTimeout to allow UI to update
        setTimeout(() => {
          try {
            this.validation = validateFilesAgainstScan(
              this.scanFileContent,
              this.images,
              this.scanType
            );
          } catch (err) {
            console.error('Validation error:', err);
            this.validation.isValidating = false;
            this.validation.hasValidated = false;
          }
        }, 100);
      },
      clearScanFile() {
        this.scanFile = null;
        this.scanFileContent = null;
        this.validation.hasValidated = false;
        // Reset the file input
        const input = document.getElementById('uploadScanFile');
        if (input) input.value = '';
      },
      getUploadXHR() {
        var xhr = new window.XMLHttpRequest();
        xhr.upload.addEventListener(
          'progress',
          function (evt) {
            if (evt.lengthComputable) {
              var progress = Math.round((evt.loaded / evt.total) * 100);
              if (progress == 100) {
                document.getElementById('progress-bar')?.removeAttribute('value');
                this.uploadingStatus = 'Uploading to Blob...';
              }
            }
          },
          false,
        );
        return xhr;
      },
      uploadGPSFiles(files) {
        if (!this.$store.state.forgeSettings || !this.$store.state.forgeSettings.gpsSettings) {
          this.$dialog.notify.warning('GPS settings not set yet, please ask admin to set it.', {
            position: 'top-right',
            timeout: 3000,
          });
          return null;
        }
        this.$store.dispatch({
          type: 'prompt_Forge',
          title: 'Add Elevation Z',
          callback: (elevation) => {
            if (!elevation || !Number.parseFloat(elevation)) {
              this.$dialog.notify.warning('Invalid value.', {
                position: 'top-right',
                timeout: 3000,
              });
              return null;
            }
            elevation = Number.parseFloat(elevation);
            let _this = this;
            _this.isUploading = true;
            _this.uploadingStatus = 'Uploading...';
            //upload images
            let formData = new FormData();
            formData.append('assetId', _this.$store.state.assetId);
            formData.append('projectName', this.targetProject.name);
            formData.append('projectId', this.targetProject.id);
            files.forEach((o) => {
              formData.append('filesToUpload', o);
            });
            formData.append('offsetZ', elevation);
            //gps
            formData.append(
              'isIndianSystem',
              this.$store.state.forgeSettings.gpsSettings.isIndianSystem,
            );
            formData.append('gpsOriginN', this.$store.state.forgeSettings.gpsSettings.originN);
            formData.append('gpsOriginE', this.$store.state.forgeSettings.gpsSettings.originE);
            formData.append('gpsA', this.$store.state.forgeSettings.gpsSettings.CONST_A);
            formData.append('gpsB', this.$store.state.forgeSettings.gpsSettings.CONST_B);
            formData.append('gpsAngle', this.$store.state.forgeSettings.gpsSettings.angle);
            formData.append('createdBy', _this.$store.state.username);
            const ctrl = new AbortController(); // timeout
            setTimeout(() => ctrl.abort(), 5000);
            HttpService.post({
              url: 'api/images360/uploadImages360GPS',
              data: formData,
              headers: {
                'Content-Type': false,
                processData: false,
              },
              onUploadProgress: function (progressEvent) {
                if (progressEvent.event.lengthComputable) {
                  var progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                  if (progress == 100) {
                    document.getElementById('progress-bar')?.removeAttribute('value');
                    _this.uploadingStatus = 'Uploading to Blob...';
                  }
                }
              },
            })
              .then((response) => {
                let data = response.data;
                _this.isUploading = false;
                if (data) {
                  if (files.length != data.length)
                    this.$dialog.notify.warning(
                      `${files.length - data.length} File/s skipped as not a GPS type.`,
                      {
                        position: 'top-right',
                        timeout: 3000,
                      },
                    );
                  data = data.map((o) => Object.assign(new Image360(), o));
                  for (var i = 0; i < data.length; i++) {
                    var img = data[i];
                    if (img.latLong.isIndianSystem)
                      img.forgeCameraPosition = convertLatLongToXYZCoordinates_Indian(img.latLong);
                    else
                      img.forgeCameraPosition = Object.assign(
                        new THREE.Vector3(),
                        img.forgeCameraPosition,
                      );
                    img.lockedForgeDirection = getRotatedDirectionByNorthAngle();
                    img.isVisibleInViewers = true;
                    _this.targetProject.images.push(img);
                    _this.targetProject.isExpanded = true;
                  }
                  this.$store.state.forgeService.sortByName(_this.targetProject.images);
                  this.$store.state.forgeService.update3dIconsInAllViews();
                }
                _this.images = [];
                $('#multiple360Images').val('');
                _this.closeDialog();
              })
              .catch((err) => {
                $handleError({ message: err });
                _this.isUploading = false;
              });
          },
          input: {
            inputName: 'Elevation Z',
            inputValue: '',
          },
        });
      },
      getImageInfo(files) {
        return Promise.all(
          Array.from(files).map((file) => {
            return new Promise((resolve, reject) => {
              const reader = new FileReader();

              reader.onload = (e) => {
                const img = new Image();

                img.onload = () => {
                  resolve({
                    name: file.name.split('.')[0],
                    width: img.width,
                    height: img.height,
                  });
                };

                img.onerror = (err) => {
                  this.$dialog.confirm({
                    text: `Error reading image ${file.name}. Do you want to continue?`,
                    title: 'Error reading image',
                    actions: {
                      false: {
                        text: 'Cancel',
                        color: 'error',
                        handle: () => {
                          reject(file, err);
                        },
                      },
                      true: {
                        text: 'Continue',
                        color: 'primary',
                        handle: () => {
                          resolve(null);
                        },
                      },
                    },
                  });
                };

                img.src = e.target.result;
              };

              reader.onerror = reject;
              reader.readAsDataURL(file);
            });
          }),
        );
      },
      async upload() {
        if (!this.targetProject) {
          this.$dialog.notify.warning('Please select the target project first.', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }
        var imagesInput = document.getElementById('multiple360Images');
        if (!imagesInput.files || imagesInput.files.length === 0) {
          this.$dialog.notify.warning('Please select 360 images first.', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }
        if (this.$store.state.projects.length === 0) {
          this.$dialog.notify.warning('Please add a project first.', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }
        if (this.scanType === 'gps') {
          this.uploadGPSFiles(Array.from(imagesInput.files));
          return;
        }
        if (!this.scanFile) {
          this.$dialog.notify.warning('Please select a scan file first.', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }

        this.isUploading = true;

        // Step 1: Read Scan File
        this.uploadingStatus = 'Step 1/3: Reading the scan file...';
        const readScanOnProgress = (percentage) => {
          this.fileProgress = percentage;
        };
        let content = await readFileContent(this.scanFile, readScanOnProgress).catch((err) => {
          error(err);
          this.uploadingStatus = `Error reading scan file.`;
        });

        if (!content) {
          this.isUploading = false;
          return;
        }

        // Step 2: Read and Process Images in Chunks
        const chunkSize = 2;
        const filesArray = Array.from(imagesInput.files);
        this.uploadingStatus = `Step 2/3: Reading and processing images in chunks of ${chunkSize}...`;

        for (let i = 0; i < filesArray.length; i += chunkSize) {
          const imageChunk = filesArray.slice(i, i + chunkSize);

          // Reading Metadata for Current Chunk
          this.uploadingStatus = `Step 2/3: Reading metadata for images (Chunk ${
            Math.floor(i / chunkSize) + 1
          } of ${Math.ceil(filesArray.length / chunkSize)})...`;

          let imageInfoList = await this.getImageInfo(imageChunk).catch((err) => {
            error(err);
            this.uploadingStatus = `Error reading image metadata for chunk ${
              Math.floor(i / chunkSize) + 1
            }.`;
          });

          if (!imageInfoList || imageInfoList.length === 0) {
            this.isUploading = false;
            return;
          }

          imageInfoList = imageInfoList.filter((o) => !!o);

          // Processing Scan Data for Current Chunk
          this.uploadingStatus = `Step 2/3: Processing scan data for images (Chunk ${
            Math.floor(i / chunkSize) + 1
          } of ${Math.ceil(filesArray.length / chunkSize)})...`;

          const images = this.$store.state.forgeService.textFileContentToImages(
            content,
            imageInfoList,
            this.scanType,
            (percentage) => {
              this.fileProgress = percentage;
            },
          );

          if (images.length === 0) {
            this.uploadingStatus = `Warning: Scan file contains no scans for chunk ${
              Math.floor(i / chunkSize) + 1
            }.`;
            this.isUploading = false;
            return;
          }

          let chunkSizeMb = (imageChunk.reduce((acc, file) => acc + file.size, 0) / 1024 / 1024).toFixed(2);


          // Uploading Current Chunk to Server
          this.uploadingStatus = `Step 3/3: Uploading chunk ${
            Math.floor(i / chunkSize) + 1
          } of ${Math.ceil(filesArray.length / chunkSize)} with ${chunkSizeMb}mb to server...`;
          await this.uploadChunk(images, imageChunk);
        }

        // Final Success Message
        this.isUploading = false;
        this.uploadingStatus =
          'Upload completed successfully! All image chunks have been processed and uploaded.';
        this.clearScanFile();
        this.images = [];
        $('#multiple360Images').val('');

        this.$dialog.notify.success('Images added successfully.', {
          position: 'top-right',
          timeout: 3000,
        });

        this.cementSelected();
        this.closeDialog();
      },
      // Helper function to upload a chunk of images
      async uploadChunk(images, files) {
        let formData = new FormData();
        formData.append('assetId', this.$store.state.assetId);
        formData.append('projectName', this.targetProject.name);
        formData.append('projectId', this.targetProject.id);
        formData.append('images', JSON.stringify(images));

        files.forEach((file) => {
          if (images.some((img) => file.name.split('.')[0] === img.name)) {
            formData.append('filesToUpload', file);
          }
        });

        return HttpService.post({
          url: 'api/images360/uploadImages',
          data: formData,
          headers: {
            'Content-Type': false,
            processData: false,
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.event.lengthComputable) {
              var progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
              this.fileProgress = progress;
              if (progress === 100) {
                this.uploadingStatus = 'Finalizing upload for current chunk...';
              }
            }
          },
        })
          .then((response) => {
            let data = response.data.map((o) => Object.assign(new Image360(), o));

            data.forEach((img) => {
              img.isVisibleInViewers = true;
              this.targetProject.images.push(img);
            });

            this.$store.state.forgeService.sortByName(this.targetProject.images);
            this.$store.state.forgeService.update3dIconsInAllViews();
          })
          .catch((err) => {
            error(err);
            this.uploadingStatus = `Error uploading chunk: ${err.message}`;
          });
      },
    },
    watch: {
      selected: {
        handler() {
          this.checkForChanges();
        },
        deep: true,
      },
    },
    computed: {
      targetProject: function () {
        // Prioritize pre-selected project if available
        if (this.preSelectedProject) {
          return this.preSelectedProject;
        }
        return this.projects.find((o) => o.name == this.selected.targetProjectName);
      },
      scanTypeLabel() {
        return getScanTypeLabel(this.scanType);
      },
    },
    components: { CenteredText, ValidationDialog },
  };
</script>

<style scoped>
  .cursor-pointer {
    cursor: pointer;
  }
</style>
